import { Box } from '@mui/material';

import { inter } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';

export const WATERMARK_SELECTOR = '[data-slide-watermark]';

interface SlideWatermarkProps {
  text: string;
  /**
   * Paint the watermark above the slide content. Opaque content, such as
   * diagram canvases, tables, and chart cards, hides a watermark behind it.
   * The multiply blend makes the result look the same as a layer behind the
   * content on a white slide: white areas show the light gray text, and dark
   * text stays dark. On a dark slide background, multiply hides the
   * watermark, so slides with a custom background keep it behind the content.
   */
  aboveContent: boolean;
}

export function SlideWatermark({ text, aboveContent }: SlideWatermarkProps) {
  return (
    <Box
      data-slide-watermark
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        // The slide sets `isolation: isolate`, so the blend stays inside the
        // slide.
        ...(aboveContent
          ? { zIndex: 2, mixBlendMode: 'multiply' }
          : { zIndex: 0 }),
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        userSelect: 'none',
        // Stack spacing gives this element a top margin. Doubling the class
        // selector outranks the Stack rule so the layer covers the full slide.
        '&&': { margin: 0 },
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: inter.style.fontFamily,
          fontSize: 144,
          fontWeight: 600,
          lineHeight: '168px',
          color: COLORS.GRAY[300],
          opacity: 0.4,
          transform: 'rotate(-30deg)',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Box>
    </Box>
  );
}
